import { selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogField
} from "components/common/FormDialog/types";
import {
  buildSettingsPayload,
  extensionSettings,
  normalizeSettingValue,
  selectedExtensionNames,
  settingFieldName,
  settingValueError,
  unofferedSettings
} from "modules/databases/extensions";
import { ExtensionInfo, ExtensionSetting } from "modules/databases/types";
import { AnySchema, object, string } from "yup";

const BOOL_VALUES = ["on", "off"];

// A hidden field is unregistered and validates as undefined. yup types these
// calls as `any`.
const optionOrNull = (allowEmpty = false): AnySchema =>
  allowEmpty
    ? object({ label: string().required(), value: string().defined() })
        .nullable()
        .default(null)
    : ((selectOptionSchema.nullable() as AnySchema).default(null) as AnySchema);

const settingHelperText = (setting: ExtensionSetting) =>
  [
    setting.description,
    (setting.min || setting.max) &&
      `Range: ${setting.min ?? "…"} – ${setting.max ?? "…"}.`,
    setting.default !== undefined && `Default: ${setting.default}.`,
    setting.specialValues?.length &&
      `Also accepts: ${setting.specialValues.join(", ")}.`,
    setting.allowEmpty && "An empty value is allowed.",
    setting.type === "size" && "Enter bytes or an explicit unit such as MB."
  ]
    .filter(Boolean)
    .join(" ");

export const settingField = (
  setting: ExtensionSetting,
  current?: string,
  prefillDefault = true
): FormDialogField => {
  const field = {
    name: settingFieldName(setting.name),
    label: setting.title ? `${setting.title} (${setting.name})` : setting.name,
    helperText: settingHelperText(setting),
    suffix: setting.unit
  };

  if (
    setting.type === "bool" ||
    setting.type === "enum" ||
    setting.type === "timezone"
  ) {
    const choices =
      setting.type === "bool" ? BOOL_VALUES : setting.values || [];
    const options = (
      setting.allowEmpty && !choices.includes("") ? ["", ...choices] : choices
    ).map((value) => ({ label: value || "Empty value", value }));
    const selected =
      current === undefined && !prefillDefault
        ? undefined
        : normalizeSettingValue(
            setting,
            current ?? (prefillDefault ? (setting.default ?? "") : "")
          );
    return {
      ...field,
      type: FIELD_TYPES.SELECT,
      options,
      defaultValue: options.find((option) => option.value === selected) ?? null,
      rules: optionOrNull(setting.allowEmpty)
    };
  }

  return {
    ...field,
    type: FIELD_TYPES.TEXT,
    defaultValue: current ?? (prefillDefault ? (setting.default ?? "") : ""),
    rules: string().test({
      name: "extensionSetting",
      test: (value, context) => {
        const error = settingValueError(setting, value ?? "");
        return error ? context.createError({ message: error }) : true;
      }
    })
  };
};

export const extensionSettingsFields = (
  catalogue: ExtensionInfo[],
  enabled: string[],
  current: Record<string, string> = {}
): FormDialogField[] => {
  const kept = Object.entries(unofferedSettings(catalogue, current));
  return [
    ...catalogue
      .filter(
        (extension) =>
          enabled.includes(extension.name) && extension.settings?.length
      )
      .flatMap((extension) => [
        {
          name: `extension:${extension.name}`,
          type: FIELD_TYPES.DIVIDER,
          label: extension.name
        },
        ...(extension.settings || []).map((setting) =>
          settingField(setting, current[setting.name])
        )
      ]),
    ...(kept.length > 0
      ? [
          {
            name: "keptSettingsInfo",
            type: FIELD_TYPES.NOTES,
            label: `ℹ️ Kept as they are: ${kept
              .map(([key, value]) => `${key}=${value}`)
              .join(", ")}`
          }
        ]
      : [])
  ];
};

// Every extension's settings, each shown only while its extension is selected in the
// same form, for a dialog that picks the extensions and their settings together.
export const pickedExtensionSettingsFields = (
  catalogue: ExtensionInfo[]
): FormDialogField[] =>
  catalogue.flatMap((extension) =>
    extensionSettingsFields([extension], [extension.name]).map((field) => ({
      ...field,
      isHidden: (fieldValues: Record<string, unknown>) =>
        !selectedExtensionNames(fieldValues.extensions).includes(extension.name)
    }))
  );

// What to send: the form's values, plus the settings no catalogue extension declares,
// which the API would otherwise drop by replacing the whole set.
export const settingsPayload = (
  catalogue: ExtensionInfo[],
  enabled: string[],
  values: Record<string, unknown>,
  current: Record<string, string> = {}
): Record<string, string> => ({
  ...unofferedSettings(catalogue, current),
  ...buildSettingsPayload(extensionSettings(catalogue, enabled), values)
});
